/**
 * Streaming Message Hook for InboundX Chat
 *
 * This hook manages real-time streaming message functionality for the InboundX chat.
 * It handles the complex flow of streaming responses from the InboundX service including:
 * - Managing streaming message state (thinking, receiving tokens, complete)
 * - Handling streaming response chunks and progressive content building
 * - Converting streaming responses to final chat messages
 * - Integrating with evaluation system for streamed messages
 * - Error handling and recovery for streaming failures
 * - Session management and conversation tracking
 * - Stream cancellation for better resource management
 *
 * The hook provides a clean interface for streaming queries while managing all
 * the underlying complexity of real-time message streaming.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
    type AssistantMessage,
    type ChatMessage,
    createAssistantMessage,
} from '../../services/chatStorage';
import {
    type BookingContext,
    type InboundXService,
    KNOWLEDGE_SOURCES_EVENT,
    type StreamingResponse,
    type VisitorProfile,
} from '../../services/inboundx';
import { setCTAOverrides } from '../../utils/content/ctaReplacer';
import { detectUserLanguage } from '../../utils/content/languageUtils';
import { getErrorMessage, getMessage } from '../../utils/content/messageTranslations';
import { logger } from '../../utils/core/logger';
import { classifyStreamingError } from '../../utils/core/streamErrorClassifier';

interface BookingState {
    is_booking_flow: boolean;
    redirect_url?: string | null;
    pending_booking_fields?: string[];
    collected_booking_fields?: Record<string, string>;
}

interface StreamingMessage {
    id: string;
    content: string;
    isComplete: boolean;
    isThinking: boolean;
    isShowingProgress?: boolean;
    visitorProfile?: VisitorProfile | null;
    bookingState?: BookingState | null;
    suggestedFollowUps?: string[];
    suggestedAnswers?: string[];
}

interface EvaluationResult {
    sessionId?: string;
    threadId?: string;
    userMessageContent?: string;
}

interface ConversationSource {
    title?: string;
    url?: string;
    snippet?: string;
    content?: string;
}

interface UseStreamingMessageProps {
    chatService: InboundXService | null;
    currentSessionId: string;
    evaluationEnabled: boolean;
    onEvaluate: (
        messageId: string,
        messageContent: string,
        rating: 'thumbs_up' | 'thumbs_down',
        result?: EvaluationResult
    ) => void;
    onTrackConversation: (
        query: string,
        output: string,
        messageId: string,
        sessionId?: string,
        threadId?: string,
        sources?: ConversationSource[],
        visitorProfile?: VisitorProfile | null,
        bookingState?: BookingState | null
    ) => void;
    onUpdateSessionId: (sessionId: string) => void;
}

export const useStreamingMessage = ({
    chatService,
    currentSessionId,
    evaluationEnabled,
    onEvaluate,
    onTrackConversation,
    onUpdateSessionId,
}: UseStreamingMessageProps) => {
    const [streamingMessage, setStreamingMessage] = useState<StreamingMessage | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const progressTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Cleanup function to cancel ongoing streams
    const cancelStream = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
            setStreamingMessage(null);
        }
        if (progressTimerRef.current) {
            clearTimeout(progressTimerRef.current);
            progressTimerRef.current = null;
        }
    }, []);

    // Cleanup on unmount — refs always hold current values, no stale closure
    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
            if (progressTimerRef.current) {
                clearTimeout(progressTimerRef.current);
            }
        };
    }, []);

    const handleStreamingQuery = useCallback(
        async (
            message: string,
            onComplete: (botMessage: ChatMessage) => void,
            onError: (errorMessage: ChatMessage) => void,
            messageSource?: string,
            bookingContext?: BookingContext,
            isFormAssistant?: boolean,
            questionContext?: string,
            isPostConversion?: boolean
        ) => {
            if (!chatService) return;

            // Cancel any ongoing stream
            cancelStream();

            // Create new abort controller
            const newController = new AbortController();
            abortControllerRef.current = newController;

            const streamingId = `streaming-${Date.now()}`;

            // Detect user language for localized messages
            const userLanguage = detectUserLanguage();

            // Set initial streaming state
            setStreamingMessage({
                id: streamingId,
                content: '',
                isComplete: false,
                isThinking: true,
            });

            // Set 15-second timer for progress message
            progressTimerRef.current = setTimeout(() => {
                setStreamingMessage((prev) =>
                    prev && !prev.isComplete
                        ? {
                              ...prev,
                              isThinking: false, // Stop showing thinking animation
                              isShowingProgress: true,
                              content: getMessage('still_processing', userLanguage),
                          }
                        : prev
                );
            }, 15000);

            let finalContent = '';
            let finalSessionId = currentSessionId;
            let finalVisitorProfile: VisitorProfile | null = null;
            let finalBookingState: BookingState | null = null;
            let finalSuggestedFollowUps: string[] = [];
            let finalSuggestedAnswers: string[] = [];
            let finalProfilingProgress: AssistantMessage['profilingProgress'] = null;
            let finalIntent: string | null = null;

            try {
                // Prepend context to message if provided (context is not shown in chat bubble)
                const trimmedContext = questionContext?.trim();
                const effectiveMessage = trimmedContext
                    ? `${trimmedContext}\n\n${message}`
                    : message;

                await chatService.queryStream(
                    effectiveMessage,
                    (chunk: StreamingResponse) => {
                        // Check if stream was cancelled
                        if (newController.signal.aborted) {
                            return;
                        }

                        if (chunk.type === 'thinking') {
                            setStreamingMessage((prev) => {
                                // Don't override progress message with thinking animation
                                if (prev?.isShowingProgress) {
                                    return prev; // Keep the progress message
                                }
                                return prev
                                    ? {
                                          ...prev,
                                          isThinking: true,
                                          content: '',
                                      }
                                    : null;
                            });
                        } else if (chunk.type === 'token') {
                            // Clear progress timer when first token arrives
                            if (progressTimerRef.current) {
                                clearTimeout(progressTimerRef.current);
                                progressTimerRef.current = null;
                            }

                            finalContent += chunk.content;
                            setStreamingMessage((prev) =>
                                prev
                                    ? {
                                          ...prev,
                                          isThinking: false,
                                          isShowingProgress: false,
                                          content: finalContent,
                                      }
                                    : null
                            );
                        } else if (chunk.type === 'complete') {
                            finalContent = chunk.content;
                            finalSessionId = chunk.sessionId || currentSessionId;
                            // Capture visitor profile from metadata if available
                            finalVisitorProfile = chunk.metadata?.visitor_profile || null;
                            logger.debug('📧 [Streaming] Completion event visitor_profile', {
                                hasVisitorProfile: !!finalVisitorProfile,
                                hasEmail: !!finalVisitorProfile?.email,
                                visitorProfileKeys: finalVisitorProfile
                                    ? Object.keys(finalVisitorProfile)
                                    : [],
                            });
                            // Capture booking state from metadata if available
                            finalBookingState = chunk.metadata?.booking_state || null;
                            // Capture suggested follow-up questions if available
                            finalSuggestedFollowUps = chunk.metadata?.suggested_follow_ups || [];
                            // Capture suggested answers if available
                            finalSuggestedAnswers = chunk.metadata?.suggested_answers || [];
                            // Capture profiling progress for post-conversion step dots
                            finalProfilingProgress = chunk.metadata?.profiling_progress || null;
                            finalIntent = chunk.metadata?.intent ?? null;

                            // Capture and apply CTA URL overrides if available
                            // These are dynamic URLs based on form data collected during conversation
                            const ctaUrlOverrides = chunk.metadata?.cta_url_overrides || null;
                            setCTAOverrides(ctaUrlOverrides);

                            // Retrieval provenance for the backoffice Playground. The server only
                            // sends it to preview surfaces, so this is undefined for real visitors.
                            // Handed to the host page as an event: the widget shows nothing, and
                            // shared code stays free of backoffice imports.
                            const knowledgeSources = chunk.metadata?.knowledge_sources;
                            if (knowledgeSources) {
                                window.dispatchEvent(
                                    new CustomEvent(KNOWLEDGE_SOURCES_EVENT, {
                                        detail: { messageId: streamingId, ...knowledgeSources },
                                    })
                                );
                            }

                            logger.debug('=== COMPLETION CHUNK ===', {
                                hasMetadata: !!chunk.metadata,
                                hasSuggestedFollowUps: !!chunk.metadata?.suggested_follow_ups,
                                followUpsCount: finalSuggestedFollowUps.length,
                                followUps: finalSuggestedFollowUps,
                                hasSuggestedAnswers: !!chunk.metadata?.suggested_answers,
                                answersCount: finalSuggestedAnswers.length,
                                answers: finalSuggestedAnswers,
                                hasCtaUrlOverrides: !!ctaUrlOverrides,
                                ctaUrlOverrides: ctaUrlOverrides,
                            });

                            setStreamingMessage((prev) =>
                                prev
                                    ? {
                                          ...prev,
                                          isComplete: true,
                                          content: finalContent,
                                          visitorProfile: finalVisitorProfile,
                                          bookingState: finalBookingState,
                                          suggestedFollowUps: finalSuggestedFollowUps,
                                          suggestedAnswers: finalSuggestedAnswers,
                                      }
                                    : null
                            );
                        }
                    },
                    (finalResponse: string) => {
                        // Check if stream was cancelled
                        if (newController.signal.aborted) {
                            return;
                        }

                        // Clear progress timer on completion
                        if (progressTimerRef.current) {
                            clearTimeout(progressTimerRef.current);
                            progressTimerRef.current = null;
                        }

                        // Streaming complete - convert to regular message with follow-ups and answers
                        const botMessage = createAssistantMessage(
                            streamingId,
                            finalResponse || finalContent,
                            {
                                timestamp: Date.now(),
                                isEvaluated: false,
                                onEvaluate: evaluationEnabled
                                    ? (rating: 'thumbs_up' | 'thumbs_down') => {
                                          onEvaluate(
                                              streamingId,
                                              finalResponse || finalContent,
                                              rating,
                                              {
                                                  sessionId: finalSessionId,
                                                  userMessageContent: message,
                                              }
                                          );
                                      }
                                    : undefined,
                                suggestedFollowUps: finalSuggestedFollowUps,
                                suggestedAnswers: finalSuggestedAnswers,
                                profilingProgress: finalProfilingProgress,
                                intent: finalIntent,
                            }
                        );

                        logger.debug('=== CREATING COMPLETED MESSAGE ===', {
                            messageId: streamingId,
                            hasSuggestedFollowUps: !!finalSuggestedFollowUps.length,
                            followUpsCount: finalSuggestedFollowUps.length,
                            followUps: finalSuggestedFollowUps,
                            hasSuggestedAnswers: !!finalSuggestedAnswers.length,
                            answersCount: finalSuggestedAnswers.length,
                            answers: finalSuggestedAnswers,
                        });

                        onComplete(botMessage);
                        setStreamingMessage(null);
                        abortControllerRef.current = null;

                        // Track conversation
                        onTrackConversation(
                            effectiveMessage,
                            finalResponse || finalContent,
                            streamingId,
                            finalSessionId,
                            undefined, // threadId not available in streaming
                            undefined, // sources not available in streaming
                            finalVisitorProfile,
                            finalBookingState
                        );

                        // Update session ID if it changed
                        if (finalSessionId && finalSessionId !== currentSessionId) {
                            onUpdateSessionId(finalSessionId);
                        }
                    },
                    (error: string) => {
                        // Check if stream was cancelled
                        if (newController.signal.aborted) {
                            return;
                        }

                        // Clear progress timer on error
                        if (progressTimerRef.current) {
                            clearTimeout(progressTimerRef.current);
                            progressTimerRef.current = null;
                        }

                        const classifiedError = classifyStreamingError(error);

                        let errorDisplayMessage: string;
                        if (classifiedError.category === 'stream_idle_timeout') {
                            logger.error('🚨 STREAMING TIMEOUT:', error);
                            errorDisplayMessage = getMessage('timeout_error', userLanguage);
                        } else if (classifiedError.category === 'network_stream_failure') {
                            logger.error('🚨 NETWORK ERROR:', error);
                            errorDisplayMessage = getMessage('network_error', userLanguage);
                        } else if (classifiedError.category === 'rate_limited') {
                            logger.warn('⏳ RATE LIMITED: another request in-flight for session');
                            errorDisplayMessage = getMessage('rate_limited', userLanguage);
                        } else if (classifiedError.category === 'validation_error') {
                            // For validation errors, log the full details to console for debugging
                            logger.error('🚨 VALIDATION ERROR:', error);
                            // Show a user-friendly message with minimal technical context
                            errorDisplayMessage = getMessage('validation_error', userLanguage);
                        } else {
                            // For other errors, log full error and include some context in user message
                            logger.error('🚨 STREAMING ERROR:', error);
                            errorDisplayMessage = getErrorMessage(
                                'generic_error',
                                userLanguage,
                                error
                            );
                        }

                        const errorMessage = createAssistantMessage(
                            streamingId,
                            errorDisplayMessage,
                            {
                                timestamp: Date.now(),
                                isError: true, // Always mark as error for styling
                            }
                        );

                        onError(errorMessage);
                        setStreamingMessage(null);
                        abortControllerRef.current = null;
                    },
                    newController.signal,
                    messageSource,
                    bookingContext,
                    isFormAssistant,
                    isPostConversion
                );
            } catch (error) {
                // Check if stream was cancelled
                if (newController.signal.aborted) {
                    return;
                }

                // Clear progress timer on error
                if (progressTimerRef.current) {
                    clearTimeout(progressTimerRef.current);
                    progressTimerRef.current = null;
                }

                // Log the full error for debugging
                logger.error('Streaming connection error:', error);

                const errorMessage = createAssistantMessage(
                    streamingId,
                    getMessage('network_error', userLanguage),
                    {
                        timestamp: Date.now(),
                        isError: true, // Mark as system error for special styling
                    }
                );
                onError(errorMessage);
                setStreamingMessage(null);
                abortControllerRef.current = null;
            }
        },
        [
            chatService,
            currentSessionId,
            evaluationEnabled,
            onEvaluate,
            onTrackConversation,
            onUpdateSessionId,
            cancelStream,
        ] // cancelStream is now stable (empty deps) so this won't cause unnecessary re-creation
    );

    return {
        streamingMessage,
        handleStreamingQuery,
        cancelStream,
    };
};
